import type { EventDetails } from "@/modules/eventDetails/client/api/types"
import type { TranslationKey } from "@/modules/i18n/components/types"
import type { FunctionComponent } from "react"

import Image from "next/image"
import { useParams } from "next/navigation"

import { Heading } from "@jobteaser/spark/components/Heading"
import { Text } from "@jobteaser/spark/components/Text"
import { Link } from "@jobteaser/ui-kit/components/Link/Link"
import { Tag } from "@jobteaser/ui-kit/components/Tag/Tag"
import { TagUniversity } from "@jobteaser/ui-kit/components/Tag/TagUniversity"

import { useEventDetailsData } from "@/modules/eventDetails/client/hooks/useEventDetailsData"
import { getEventTypeTranslation } from "@/modules/events/client/utils/getEventTypeTranslation"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useTrackEvent } from "@/modules/tracking/useTrackEvent"

import styles from "./Header.module.css"
import layoutStyles from "@/modules/eventDetails/client/components/Layout/Layout.module.css"

export const Header: FunctionComponent = () => {
  const { t } = useTranslation(["event_details"])
  const { eventSlug } = useParams<{ eventSlug: string }>()
  const event = useEventDetailsData(eventSlug)
  const trackEvent = useTrackEvent()

  if (!event) {
    return null
  }

  const { title, kind, careerCenter, ownership, isExclusive, companies } = event as EventDetails

  const eventType = getEventTypeTranslation(kind) as TranslationKey<"event_details">

  const hasCompaniesAttending = companies !== undefined && companies.length > 0
  const companyIds = hasCompaniesAttending ? companies.map(company => company.id) : []
  const isCompanyEvent = ownership.type === "company"
  const ownerCompany =
    isCompanyEvent && hasCompaniesAttending ? companies.find(company => company.id === ownership.id) : undefined
  const isPartner =
    isCompanyEvent && hasCompaniesAttending && companyIds.includes(ownership.id)
      ? companies.find(company => company.id)?.isPartner
      : false

  const handleCompanyLogoClick = (): void => {
    if (ownerCompany) {
      trackEvent("event_ad_logo_click", {
        company_id: ownerCompany.id,
        company_name: ownerCompany.name,
        event_id: event.id,
        position: "top",
        query_parameters: "",
        service: "event_details",
      })
    }
  }

  return (
    <section className={layoutStyles.header} data-testid="event-details-header">
      <div className={styles.eventInfo__container}>
        <Tag className={styles.eventInfo__eventType} data-testid="event-details-header-event-type" size="small">
          {eventType && t(eventType)}
        </Tag>
        {isExclusive && careerCenter && (
          <TagUniversity
            className={styles.eventInfo__exclusive}
            label={t("event_details.exclusive")}
            universityLogo={careerCenter.logoUrl}
            data-testid="event-details-header-exclusivity"
          />
        )}
      </div>
      <Heading as="h1" variant="title1" className={styles.title} weight="bold" data-testid="event-details-header-title">
        {title}
      </Heading>
      <div className={styles.organiser__container}>
        {ownerCompany && (
          <Link href={ownerCompany.url} onClick={handleCompanyLogoClick} aria-labelledby="organizer">
            <Image
              className={styles.organiser__logo}
              src={ownership.logo}
              alt=""
              priority
              width={80}
              height={80}
              data-testid="event-details-header-logo"
            />
          </Link>
        )}
        {!ownerCompany && (
          <Image
            className={styles.organiser__logo}
            src={ownership.logo}
            alt=""
            priority
            width={80}
            height={80}
            data-testid="event-details-header-logo"
          />
        )}
        <div className={styles.organiser__wrapper} data-testid="event-details-header-organisation">
          {isPartner && careerCenter && (
            <TagUniversity
              className={styles.organiser__partner}
              label={t("event_details.header.partner")}
              universityLogo={careerCenter.logoUrl}
              data-testid="event-details-header-partner"
            />
          )}
          <Text variant="body2" data-testid="event-details-header-organiser">
            {t("event_details.header.organiser")}
          </Text>
          <Heading variant="title3" weight="bold" data-testid="event-details-header-organiser-name" id="organizer">
            {ownership.name}
          </Heading>
        </div>
      </div>
    </section>
  )
}
